/**
 * WebSmHandler — pick one of two handlers (ported from Glow's
 * WebSmHandler_type.jsx). Two figures at the edges; tapping opens a
 * bottom-sheet with name/bio + Choose. Left → CLICK1, right → CLICK2.
 * Options: Title / Subtitle / Handler{1,2}Name / Handler{1,2}Image
 *   / Handler{1,2}ImageLook / Handler{1,2}Desc / Handler{1,2}Color / BgColor
 */
import { useEffect, useState, type CSSProperties } from "react";
import { HandlerExchange } from "./handler-exchange";
import { Counterstrike } from "./counterstrike";
import {
  HANDLER_MISSION_API,
  type HandlerMissionFlow,
} from "./handler-mission";
import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { useFunnelStore } from "@/store";
import { HandlerPromotion } from "./handler-promotion";
import { HandlerConversation } from "./handler-conversation";
import { useHandlerCompanionEnabled } from "./companion-config";
import { ACTION_BTN_CLASS, stepVars } from "./step-shell";
import type { StepTypeProps } from "./types";
import veronicaNoLook from "./veronica-nolook.png";
import veronicaLook from "./veronica-look.png";
import dariusNoLook from "./darius-nolook.png";
import dariusLook from "./darius-look.png";

const Figure = ({
  side,
  image,
  name,
  tone,
  flip,
  hovered,
  dimmed,
  onEnter,
  onLeave,
  onClick,
}: {
  side: "left" | "right";
  image: string;
  name: string;
  tone: string;
  flip: boolean;
  hovered: boolean;
  dimmed: boolean;
  onEnter: () => void;
  onLeave: () => void;
  onClick: () => void;
}) => {
  // Each handler's tone is authored, and it drives both halves of the rim
  // light, so it rides along as a custom property. `side` picks which edge the
  // figure hugs, which no static class can express.
  const vars = {
    "--sm-tone-near": `${tone}bb`,
    "--sm-tone-far": `${tone}66`,
    [side]: 0,
  } as CSSProperties;
  const lifted = hovered && !dimmed;
  return (
    <button
      type="button"
      aria-label={name}
      data-testid={`handler-${side}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={onClick}
      style={vars}
      // `translate`, not `transform` — Tailwind v4's -translate-y sets the
      // standalone property, so a transform transition would never fire.
      className={`absolute bottom-[36%] z-[2] w-[min(58cqw,430px)] cursor-pointer border-0 bg-transparent p-0 [transition:opacity_.3s_ease,filter_.3s_ease,translate_.3s_ease] ${
        dimmed ? "opacity-40" : "opacity-100"
      } ${lifted ? "-translate-y-[5px] brightness-[1.12]" : "translate-y-0 brightness-[0.92]"}`}
    >
      {/* the pool of shadow they stand in */}
      <span className="absolute right-[10%] bottom-0.5 left-[10%] h-6 rounded-[50%] bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,.55),transparent_72%)]" />
      <img
        src={image}
        alt={name}
        className={`pointer-events-none block h-auto w-full [filter:drop-shadow(0_0_9px_var(--sm-tone-near))_drop-shadow(0_0_20px_var(--sm-tone-far))] ${
          flip ? "-scale-x-100" : ""
        }`}
      />
    </button>
  );
};

export default function WebSmHandlerType({ getOpt, dispatch }: StepTypeProps) {
  const [acceptedMission, setAcceptedMission] =
    useState<HandlerMissionFlow | null>(null);
  const [resuming, setResuming] = useState(false);
  const promotionComplete = useFunnelStore((s) => s.handlerPromotionComplete);
  const setPromotionComplete = (value: boolean) =>
    useFunnelStore.setState({ handlerPromotionComplete: value });
  const [restoring, setRestoring] = useState(true);
  const [choosing, setChoosing] = useState(false);
  const [choiceError, setChoiceError] = useState("");
  const [introduction, setIntroduction] = useState<{
    name: string;
    event: string;
    portrait: string;
  } | null>(null);
  const choose = async (name: string, event: string, portrait: string) => {
    if (!companionEnabled) {
      dispatch(event);
      return;
    }
    if (choosing) return;
    setChoosing(true);
    setChoiceError("");
    try {
      await apiClient.post(API_ENDPOINTS.companion.open, { handler: name });
      useFunnelStore.setState({
        handler: name,
        handlerPromotionComplete: true,
      });
      setIntroduction({ name, event, portrait });
    } catch {
      setChoiceError("Couldn't save your handler. Please try choosing again.");
    } finally {
      setChoosing(false);
    }
  };
  const companionEnabled = useHandlerCompanionEnabled();
  /* eslint-disable react-hooks/set-state-in-effect -- sync bail-out before async restore */
  useEffect(() => {
    if (!companionEnabled) {
      setRestoring(false);
      return;
    }
    const controller = new AbortController();
    void apiClient
      .get<{ result: { flow: HandlerMissionFlow | null } }>(
        HANDLER_MISSION_API,
        { signal: controller.signal },
      )
      .then(async ({ data }) => {
        const selection = await apiClient.get<{
          result: { handler: string | null };
        }>(API_ENDPOINTS.companion.open, { signal: controller.signal });
        if (controller.signal.aborted) return;
        const name = selection.data.result.handler;
        if (name !== "Darius" && name !== "Veronica") return;
        setIntroduction({
          name,
          event: name === "Darius" ? "CLICK2" : "CLICK1",
          portrait: name === "Darius" ? dariusLook : veronicaLook,
        });
        setPromotionComplete(true);
        setResuming(
          data.result.flow?.phase === "review" ||
            data.result.flow?.phase === "discover" ||
            data.result.flow?.phase === "filed",
        );
        if (data.result.flow?.phase === "accepted")
          setAcceptedMission(data.result.flow);
      })
      .catch(() => {
        /* A new member may not yet be eligible for a handler mission. */
      })
      .finally(() => {
        if (!controller.signal.aborted) setRestoring(false);
      });
    return () => controller.abort();
  }, [companionEnabled]);
  /* eslint-enable react-hooks/set-state-in-effect */
  const bg = getOpt("BgColor") || "#0a0809";
  const title = getOpt("Title") || "Choose your handler.";
  const subtitle =
    getOpt("Subtitle") ||
    "The voice on the other end of your texts — your briefing, your backup, your way out.";

  const [hover, setHover] = useState<"left" | "right" | null>(null);
  const [selected, setSelected] = useState<"left" | "right" | null>(null);

  const left = {
    name: getOpt("Handler1Name") || "Veronica",
    nolook: getOpt("Handler1Image") || veronicaNoLook,
    look: getOpt("Handler1ImageLook") || veronicaLook,
    desc:
      getOpt("Handler1Desc") ||
      "Ex-cryptographer. Ran quiet ops in three capitals and never lost an agent. Speaks five languages; lies fluently in none of them.",
    tone: getOpt("Handler1Color") || "#a9cdf0",
    onChoose: () => {
      choose(
        getOpt("Handler1Name") || "Veronica",
        "CLICK1",
        getOpt("Handler1ImageLook") || veronicaLook,
      );
    },
  };
  const right = {
    name: getOpt("Handler2Name") || "Darius",
    nolook: getOpt("Handler2Image") || dariusNoLook,
    look: getOpt("Handler2ImageLook") || dariusLook,
    desc:
      getOpt("Handler2Desc") ||
      "Former stage magician who plans six moves ahead and texts like he has all the time in the world. He doesn't.",
    tone: getOpt("Handler2Color") || "#e0b357",
    onChoose: () =>
      choose(
        getOpt("Handler2Name") || "Darius",
        "CLICK2",
        getOpt("Handler2ImageLook") || dariusLook,
      ),
  };
  const current = ({ left, right } as const)[selected ?? "left"];
  const showSheet = selected !== null;

  // Both handlers wash the backdrop from their own side, so the section carries
  // both tones; the sheet below reads whichever one is selected.
  const sectionVars = {
    "--sm-bg": bg,
    "--sm-left-wash": `${left.tone}1c`,
    "--sm-right-wash": `${right.tone}1c`,
  } as CSSProperties;
  // stepVars supplies the gold edge and label colour the red action button reads.
  const sheetVars = {
    ...stepVars({ accent: getOpt("AccentColor") || "#d6a94e" }),
    "--sm-tone": current.tone,
    "--sm-tone-edge": `${current.tone}77`,
    "--sm-tone-halo": `${current.tone}22`,
    "--sm-tone-glow": `${current.tone}88`,
  } as CSSProperties;

  const [enemyError, setEnemyError] = useState("");
  const [enemyAttempt, setEnemyAttempt] = useState(0);
  useEffect(() => {
    if (!acceptedMission || acceptedMission.enemyReady !== false) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const started = Date.now();
    const poll = async () => {
      try {
        const { data } = await apiClient.get<{
          result: { flow: HandlerMissionFlow | null };
        }>(HANDLER_MISSION_API);
        if (cancelled) return;
        if (
          data.result.flow?.id === acceptedMission.id &&
          data.result.flow.enemyReady
        ) {
          setAcceptedMission(data.result.flow);
          return;
        }
        if (Date.now() - started > 120000) {
          setEnemyError(
            "The Enemy briefing is taking longer than expected. Your accepted mission is saved.",
          );
          return;
        }
      } catch {
        if (!cancelled)
          setEnemyError(
            "Connection interrupted. Your accepted mission is saved.",
          );
      }
      if (!cancelled) timer = setTimeout(poll, 2000);
    };
    // ponytail: sync reset before async poll — intentional, no async alternative
    /* eslint-disable react-hooks/set-state-in-effect */
    setEnemyError("");
    /* eslint-enable react-hooks/set-state-in-effect */
    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [acceptedMission?.id, acceptedMission?.enemyReady, enemyAttempt]);
  if (acceptedMission?.enemyReady === false)
    return (
      <section className="handler-conversation">
        <p role="status">Preparing your Enemy briefing…</p>
        {enemyError && (
          <div role="alert">
            <p>{enemyError}</p>
            <button onClick={() => setEnemyAttempt((n) => n + 1)}>
              Check again
            </button>
          </div>
        )}
      </section>
    );
  if (companionEnabled && restoring)
    return (
      <section className="handler-conversation">
        <p role="status">Resuming your handler…</p>
      </section>
    );
  if (choosing)
    return (
      <section className="handler-conversation">
        <p role="status">Connecting to your handler…</p>
      </section>
    );
  if (acceptedMission)
    return (
      <Counterstrike
        tier="beginner"
        mission={acceptedMission.mission}
        acceptLabel="Start my mission"
        onAccept={() => {
          useFunnelStore.setState({
            handler: introduction?.name ?? null,
            missionId: 0,
            filedMissionNo: null,
            tier: "beginner",
            acceptedAt:
              Date.parse(acceptedMission.acceptedAt || "") || Date.now(),
          });
          window.dispatchEvent(
            new CustomEvent("sm:handler-introduced", {
              detail: introduction?.name,
            }),
          );
          dispatch("CUSTOMMISSION");
        }}
      />
    );

  if (introduction)
    return (
      <HandlerConversation
        missionFlow
        returning={resuming}
        onMissionAccepted={setAcceptedMission}
        name={introduction.name}
        portrait={introduction.portrait}
        onContinue={() => dispatch(introduction.event)}
      />
    );

  if (companionEnabled && !promotionComplete)
    return <HandlerPromotion onContinue={() => setPromotionComplete(true)} />;

  if (companionEnabled)
    return (
      <>
        <HandlerExchange candidates={[left, right]} />
        {choiceError && (
          <p
            role="alert"
            style={{
              position: "fixed",
              bottom: 8,
              left: 16,
              right: 16,
              background: "#242424",
              padding: 12,
              color: "#ead199",
              zIndex: 20,
            }}
          >
            {choiceError}
          </p>
        )}
      </>
    );

  return (
    <section
      style={sectionVars}
      className="relative h-[100dvh] overflow-hidden font-sans bg-[radial-gradient(700px_460px_at_22%_14%,var(--sm-left-wash),transparent_60%),radial-gradient(700px_460px_at_78%_14%,var(--sm-right-wash),transparent_60%),radial-gradient(900px_600px_at_50%_120%,rgba(0,0,0,.5),transparent_60%)] bg-[var(--sm-bg)]"
    >
      <div className="pointer-events-none absolute top-[30px] right-0 left-0 z-[3] px-4 text-center">
        <div className="font-stencil text-[clamp(15px,4.2cqw,20px)] leading-[1.3] tracking-[1.5px] text-[#eadfc6] uppercase [text-shadow:0_0_18px_rgba(214,169,78,.3),0_2px_14px_rgba(0,0,0,.9)]">
          <span className="text-[#d6a94e]">▸</span> {title}{" "}
          <span className="text-[#d6a94e]">◂</span>
        </div>
        {subtitle ? (
          <div className="mx-auto mt-2.5 max-w-[420px] text-[14.5px] leading-[1.5] text-white/70 [text-shadow:0_1px_10px_rgba(0,0,0,.7)]">
            {subtitle}
          </div>
        ) : null}
      </div>

      <Figure
        side="left"
        image={left.look}
        name={left.name}
        tone={left.tone}
        flip={false}
        hovered={hover === "left"}
        dimmed={selected === "right"}
        onEnter={() => setHover("left")}
        onLeave={() => setHover((h) => (h === "left" ? null : h))}
        onClick={() => setSelected("left")}
      />
      <Figure
        side="right"
        image={right.look}
        name={right.name}
        tone={right.tone}
        flip={true}
        hovered={hover === "right"}
        dimmed={selected === "left"}
        onEnter={() => setHover("right")}
        onLeave={() => setHover((h) => (h === "right" ? null : h))}
        onClick={() => setSelected("right")}
      />

      {showSheet ? (
        <div
          style={sheetVars}
          className="absolute right-0 bottom-0 left-0 z-[6] animate-[smSheetUp_.4s_cubic-bezier(.3,.9,.3,1)_both] px-3 pb-[14px]"
        >
          <div className="relative mx-auto max-w-[460px] rounded-2xl border border-[var(--sm-tone-edge)] bg-[rgb(18_16_18/0.96)] px-[22px] pt-[22px] pb-5 text-center shadow-[0_-10px_50px_rgba(0,0,0,.6),0_0_34px_var(--sm-tone-halo)]">
            <button
              type="button"
              onClick={() => setSelected(null)}
              aria-label="Close"
              className="absolute top-2.5 right-3 cursor-pointer border-none bg-none text-xl leading-none text-white/50"
            >
              ×
            </button>
            <div className="font-stencil text-xl tracking-[2px] text-[var(--sm-tone)] [text-shadow:0_0_16px_var(--sm-tone-glow)]">
              {(current.name || "").toUpperCase()}
            </div>
            <div className="mx-auto mt-3 mb-5 max-w-[360px] text-[14.5px] leading-[1.55] text-white/80">
              {current.desc}
            </div>
            <button
              type="button"
              onClick={current.onChoose}
              className={`${ACTION_BTN_CLASS} rounded-lg px-[26px] py-3 text-[14.5px] font-semibold tracking-[0.3px]`}
            >
              Choose {current.name}
            </button>
          </div>
        </div>
      ) : null}

      {/* No underscore in the name: Tailwind reads `_` in an arbitrary value
          as a space, so `sm_sheetUp` would never match this keyframe. */}
      <style>{`
        @keyframes smSheetUp{from{opacity:0;transform:translateY(40px)}to{opacity:1;transform:none}}
      `}</style>
    </section>
  );
}
